import { EXPEDIENTE_SOURCE_IDS } from "@tribunalea/shared";

function pjnDocumentByAction(documents) {
  return new Map(
    documents
      .filter(({ actionId }) => actionId)
      .map((document) => [String(document.actionId), document]),
  );
}

function pjnRelatedRow(row, index, relationType = null) {
  const fields = row?.fields || {};
  const entries = Object.entries(fields);
  const valueFor = (...labels) => entries.find(([label]) =>
    labels.some((candidate) =>
      label.toLocaleLowerCase("es-AR").includes(candidate),
    ),
  )?.[1] || null;
  return {
    preExpId: row?.id || `pjn-related:${index}`,
    cuij: valueFor("expediente", "número", "numero", "causa"),
    caratula: valueFor("carátula", "caratula"),
    tipo: relationType || valueFor("tipo", "relación", "relacion"),
    fecha: valueFor("fecha"),
    sourceFields: fields,
  };
}

export function normalizeNationalExpediente(expediente) {
  const content = {
    participants: [],
    actions: [],
    documents: [],
    relatedCases: [],
    appeals: [],
    ...expediente.content,
  };
  const documents = pjnDocumentByAction(content.documents);
  const queriedAt = new Date().toISOString();
  return {
    ...expediente,
    identifier: expediente.detail?.identifier || expediente.externalId,
    cuij: expediente.detail?.identifier || expediente.externalId,
    fechaInicio: null,
    objetoJuicios: [],
    partes: content.participants.map((participant) => ({
      perId: participant.id,
      nombreApellido: participant.name,
      vinculo: participant.type,
      tipoDocumento: null,
      numeroDocumento: null,
      domicilios: [],
      representantes: (participant.representatives || []).map(
        (representative) => ({
          nombreApellido: representative.name,
          vinculo: representative.role,
          matricula: representative.registration,
          iej: representative.iej,
          domicilios: [],
        }),
      ),
    })),
    actuaciones: content.actions.map((action) => {
      const document = documents.get(String(action.id));
      const type = String(action.type || "").toLocaleLowerCase("es-AR");
      return {
        actId: action.id,
        mexpId: action.id,
        tipo: action.type,
        titulo: action.description,
        fechaPub: action.date,
        fechaFirma: action.date,
        dateOnly: true,
        organismoCreador: action.office,
        firmantes: null,
        numero: null,
        poseeAdjunto: Boolean(document),
        documentAvailable: Boolean(document),
        documentOpenable: false,
        sourceDocumentId: document?.id || null,
        esCedula: type.includes("cedula") || type.includes("cédula"),
        esEscrito: type.includes("escrito"),
        esDespacho: type.includes("despacho"),
        estadoNotificacion: null,
        pages: action.pages,
      };
    }),
    documentos: content.documents,
    movimientos: [],
    relacionados: [
      ...content.relatedCases.map((row, index) =>
        pjnRelatedRow(row, index),
      ),
      ...content.appeals.map((row, index) =>
        pjnRelatedRow(row, index, "Recurso"),
      ),
    ],
    actuacionesCount: content.actions.length,
    movimientosCount: 0,
    isSaved: false,
    isArchived: false,
    isFollowed: false,
    basicRefreshedAt: queriedAt,
    detailsRefreshedAt: queriedAt,
    dataSync: {
      basic: { status: "ready", updatedAt: queriedAt, error: null },
      details: { status: "ready", updatedAt: queriedAt, error: null },
    },
  };
}

const mendozaAdapter = Object.freeze({
  id: EXPEDIENTE_SOURCE_IDS.MENDOZA,
  manageable: true,
  identifierLabel: "CUIJ",
  async getCase(api, identifier) {
    return api.getExpediente(identifier);
  },
  async getTimeline(api, identifier) {
    return api.getExpedienteTimeline(identifier);
  },
  externalUrl(expediente) {
    const params = new URLSearchParams({
      identificador: expediente.cuij,
      open: "false",
      tituloBusqueda: "Causas",
      tipoBusqueda: "CAU",
    });
    return `https://iol.jus.mendoza.gov.ar/iol-ui/p/expedientes?${params}`;
  },
});

const nationalAdapter = Object.freeze({
  id: EXPEDIENTE_SOURCE_IDS.NATIONAL,
  manageable: false,
  identifierLabel: "Número de expediente",
  async getCase(api, identifier) {
    return normalizeNationalExpediente(
      await api.getExpedienteFromSource(identifier),
    );
  },
  async getTimeline(_api, _identifier, expediente) {
    return {
      actuaciones: expediente.actuaciones || [],
      movimientos: expediente.movimientos || [],
      actuacionesCount: expediente.actuacionesCount || 0,
      movimientosCount: expediente.movimientosCount || 0,
      dataSync: expediente.dataSync,
      detailsRefreshedAt: expediente.detailsRefreshedAt,
      praxisRefreshedAt: expediente.praxisRefreshedAt,
    };
  },
  externalUrl() {
    return "https://scw.pjn.gov.ar/scw/home.seam";
  },
});

const adapters = Object.freeze({
  [mendozaAdapter.id]: mendozaAdapter,
  [nationalAdapter.id]: nationalAdapter,
});

export function expedienteSourceAdapterFor(sourceId) {
  const adapter = adapters[sourceId];
  if (!adapter) {
    throw new TypeError(`No existe un adaptador de expediente para ${sourceId}.`);
  }
  return adapter;
}
